<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useEventListener } from '@vueuse/core';
import { ArrowPathIcon, CommandLineIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import SavedViewIcon from '@/components/savedViews/SavedViewIcon.vue';
import { useSavedViewAppearances } from '@/composables/useSavedViewAppearances';
import { modelIconForRow } from '@/composables/modelIcons';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import BugReportDialog from '@/components/BugReport/BugReportDialog.vue';
import { useAuthStore } from '@/stores/authStore';
import { useRecentItemsStore } from '@/stores/recentItemsStore';
import { useCommandPaletteStore } from '@/components/commandPalette/commandPaletteStore';
import { useQuickSearch } from '@/components/commandPalette/useQuickSearch';
import {
    PALETTE_TABS,
    buildPaletteSections,
    navigationCommands,
    paletteSnippet,
    tabSearchedEntityIds,
    type PaletteCommand,
    type PaletteItem,
    type PaletteTabId,
} from '@/components/commandPalette/groupQuickSearchResults';
import { PALETTE_HIGHLIGHT, quickSearchTerms } from '@/utils/highlightMatch';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import QuickSearchFieldsChip from '@/components/search/QuickSearchFieldsChip.vue';
import QuickSearchFieldsPanel from '@/components/search/QuickSearchFieldsPanel.vue';
import { entityGate, savedViewIsOpenable, viewPathFor, visibleAreas } from '@/navigation/areas';
import { areaEntry } from '@/navigation/areaSpots';
import { isGateOpen } from '@/navigation/gates';
import { flattenSavedViewPages, useAllSavedViewsQuery } from '@/composables/api/useSavedViewsApi';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';

/**
 * Cmd+K (Ctrl+K on Windows): one search box from anywhere. Before you type it
 * lists commands and the records you opened lately. Typing also finds "Go to"
 * each list and page of the sidebar. From two characters it
 * searches players, zones, locations, accounts, live tracks and saved views.
 * Tab moves between categories, the arrows move, Enter opens the record's page,
 * Cmd+Enter opens it in the side peek and Esc closes.
 *
 * Laid out like Macro's palette, in the same raised panel as Create New. Every
 * row that goes somewhere is a real link, so Cmd-click or a middle click opens
 * it in a new tab.
 */

const palette = useCommandPaletteStore();
const router = useRouter();
const authStore = useAuthStore();
const recentItems = useRecentItemsStore();
const { openRecord } = useOpenRecordInInspector();

const query = ref('');
const tab = ref<PaletteTabId>('all');
const highlighted = ref(0);
const bugReportOpen = ref(false);
const inputEl = ref<HTMLInputElement | null>(null);

const search = useQuickSearch(query);

// Every saved view the user can open, on a list or on a page such as Track
// matching, for the "Go to" commands. Views of a type or page the user can no
// longer open are left out, as the sidebar leaves them out.
const allViews = useAllSavedViewsQuery();
const appearanceOf = useSavedViewAppearances();
const openableViews = computed(() =>
    flattenSavedViewPages(allViews.data.value).filter(
        (view) => viewPathFor(view.entity, view.id) !== null && savedViewIsOpenable(view.entity, isGateOpen),
    ),
);

const commands = computed<PaletteCommand[]>(() => [
    ...navigationCommands(visibleAreas(isGateOpen), (area) => areaEntry(area, authStore.userData?.id)),
    ...openableViews.value.map((view) => ({
        id: `view-${view.id}`,
        label: `Go to ${view.name}`,
        to: viewPathFor(view.entity, view.id)!,
    })),
    { id: 'report-bug', label: 'Report a bug', action: 'report-bug' as const },
]);

const result = computed(() =>
    buildPaletteSections({
        query: query.value,
        tab: tab.value,
        commands: commands.value,
        // A record or view the user can no longer open drops out, rather than linking to not found.
        recents: recentItems.items.filter((item) =>
            item.kind === 'view' ? savedViewIsOpenable(item.entityId, isGateOpen) : isGateOpen(entityGate(item.entityId)),
        ),
        response: search.response.value,
        failed: search.failed.value,
    }),
);
const flatItems = computed(() => result.value.sections.flatMap((section) => section.items));
const unavailableMessage = computed(() =>
    search.rateLimited.value ? 'Too many searches. Wait a moment and try again.' : result.value.unavailableMessage,
);

watch([query, tab], () => {
    highlighted.value = 0;
});

watch(
    () => palette.isOpen,
    (isOpen) => {
        if (isOpen) {
            query.value = '';
            tab.value = 'all';
            nextTick(() => inputEl.value?.focus());
        }
    },
);

// Cmd+K or Ctrl+K opens and closes it from any page while signed in.
useEventListener(window, 'keydown', (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && authStore.userData) {
        event.preventDefault();
        palette.toggle();
    }
});

function run(item: PaletteItem, inSidePeek = false) {
    palette.close();
    if (item.action === 'report-bug') {
        bugReportOpen.value = true;
        return;
    }
    if (inSidePeek && item.peek) {
        openRecord(item.peek.entityId, item.peek.id);
        return;
    }
    if (item.to) {
        router.push(item.to);
    }
}

function onKeydown(event: KeyboardEvent) {
    // Ignore Enter while an input method is still composing a word.
    if (event.isComposing) return;
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        highlighted.value = Math.min(highlighted.value + 1, flatItems.value.length - 1);
    } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        highlighted.value = Math.max(highlighted.value - 1, 0);
    } else if (event.key === 'Enter') {
        const item = flatItems.value[highlighted.value];
        if (item) {
            event.preventDefault();
            run(item, event.metaKey || event.ctrlKey);
        }
    } else if (event.key === 'Tab') {
        event.preventDefault();
        const index = PALETTE_TABS.findIndex((candidate) => candidate.id === tab.value);
        const step = event.shiftKey ? -1 : 1;
        tab.value = PALETTE_TABS[(index + step + PALETTE_TABS.length) % PALETTE_TABS.length].id;
    }
}

/**
 * A record shows its own icon, such as a player's type or a zone's brand, and a command a
 * prompt. A view draws SavedViewIcon instead.
 */
function iconFor(item: PaletteItem) {
    if (item.kind === 'record') return modelIconForRow(item.entityId, item.iconRow ?? null) ?? MagnifyingGlassIcon;
    return CommandLineIcon;
}

/** A plain click opens the link here and closes the palette. A modified click is the browser's: a new tab. */
function onItemClick(item: PaletteItem, event: MouseEvent) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (!item.to) {
        run(item);
        return;
    }
    palette.close();
}

function indexOf(item: PaletteItem): number {
    return flatItems.value.indexOf(item);
}

const user = computed(() => ({ name: authStore.userData?.name, email: authStore.userData?.email }));

// Quick search fields (SAU-467): the chip in the box opens one tag row for each record
// type the open tab searches, and each hit highlights what it matched.
const fieldsOpen = ref(false);
const fieldsEntityIds = computed(() => tabSearchedEntityIds(tab.value));
/** The typed terms, for a record's highlight. */
function termsFor(item: PaletteItem): string[] {
    return item.kind === 'record' && item.entityId ? quickSearchTerms(query.value) : [];
}
</script>

<template>
  <Dialog :open="palette.isOpen" @update:open="(value: boolean) => (value ? palette.open() : palette.close())">
    <!-- Above a phone the window starts 10vh down and stops at 80vh, so it always ends 10vh above the bottom.
         The search box, the tabs and the footer keep their height. The field tags and the results shrink and scroll.
         While the field tags are open the results keep 10rem, or a quarter of a short screen, so a few rows show. -->
    <DialogContent
      hide-close-button
      class="top-[10vh] flex max-h-[80vh] max-w-[calc(100vw-1rem)] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-[54rem] max-md:top-0 max-md:left-0 max-md:h-dvh max-md:max-h-none max-md:w-screen max-md:max-w-none max-md:translate-x-0 max-md:rounded-none"
      data-testid="command-palette"
    >
      <DialogTitle class="sr-only">Search everything</DialogTitle>
      <DialogDescription class="sr-only">Search records and saved views, or run a command.</DialogDescription>
      <label class="flex h-16 shrink-0 items-center gap-3 border-b border-border/50 px-5">
        <MagnifyingGlassIcon class="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          ref="inputEl"
          v-model="query"
          type="search"
          aria-label="Search everything"
          placeholder="Search…"
          data-testid="command-palette-input"
          class="min-w-0 flex-1 bg-transparent text-lg outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
          @keydown="onKeydown"
        />
        <QuickSearchFieldsChip
          v-if="fieldsEntityIds.length > 0"
          v-model:open="fieldsOpen"
          :entity-ids="fieldsEntityIds"
          panel-id="command-palette-fields"
        />
      </label>
      <div v-if="fieldsOpen && fieldsEntityIds.length > 0" class="flex min-h-0 flex-col border-b border-border/50 px-5 py-3">
        <QuickSearchFieldsPanel id="command-palette-fields" :entity-ids="fieldsEntityIds" />
      </div>
      <div role="tablist" aria-label="Categories" class="flex shrink-0 gap-1 overflow-x-auto px-3 pt-3 pb-1">
        <button
          v-for="candidate in PALETTE_TABS"
          :key="candidate.id"
          type="button"
          role="tab"
          :aria-selected="tab === candidate.id"
          :data-testid="`command-palette-tab-${candidate.id}`"
          class="h-9 shrink-0 rounded-full px-4 text-sm text-muted-foreground hover:text-foreground aria-selected:bg-foreground/[0.06] aria-selected:text-foreground aria-selected:shadow-[inset_0_0_0_1px_var(--border)]"
          @click="tab = candidate.id"
        >
          {{ candidate.label }}
        </button>
      </div>
      <!-- On a phone the palette fills the screen, as the spec asks, and the results take the rest of it. -->
      <div
        :class="['max-h-[55vh] overflow-y-auto p-2 max-md:max-h-none max-md:min-h-0 max-md:flex-1', fieldsOpen && 'min-h-[min(10rem,25vh)]']"
        role="listbox"
        aria-label="Results"
        data-testid="command-palette-results"
      >
        <section v-for="section in result.sections" :key="section.label" data-testid="command-palette-section">
          <h3 class="px-3 pt-3 pb-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">{{ section.label }}</h3>
          <component
            :is="item.to ? 'router-link' : 'button'"
            v-for="item in section.items"
            :key="item.key"
            v-bind="item.to ? { to: item.to } : { type: 'button' }"
            role="option"
            :aria-selected="indexOf(item) === highlighted"
            data-testid="command-palette-item"
            class="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-[15px] font-medium text-secondary-foreground/80 aria-selected:bg-foreground/[0.06] aria-selected:text-foreground"
            @mouseenter="highlighted = indexOf(item)"
            @click="onItemClick(item, $event)"
          >
            <!-- A shared view from the library has no id of the user's own, so it keeps the bookmark. -->
            <SavedViewIcon
              v-if="item.kind === 'view'"
              :appearance="item.viewId === undefined ? null : appearanceOf(item.viewId)"
              class="size-4"
              data-testid="command-palette-item-icon"
            />
            <component
              :is="iconFor(item)"
              v-else
              :class="['size-4 shrink-0', item.kind === 'command' && 'text-muted-foreground']"
              aria-hidden="true"
              data-testid="command-palette-item-icon"
            />
            <span class="flex min-w-0 flex-1 flex-col py-1">
              <span class="truncate">
                <span v-if="item.leadingId !== undefined" class="mr-2 text-muted-foreground tabular-nums" data-testid="command-palette-item-id">
                  <SearchHighlight :name="PALETTE_HIGHLIGHT" :terms="termsFor(item)">{{ item.leadingId }}</SearchHighlight>
                </span>
                <SearchHighlight :name="PALETTE_HIGHLIGHT" :terms="termsFor(item)">{{ item.label }}</SearchHighlight>
              </span>
              <span v-if="item.detail" class="truncate text-xs font-normal text-muted-foreground">
                <SearchHighlight :name="PALETTE_HIGHLIGHT" :terms="termsFor(item)">{{ item.detail }}</SearchHighlight>
              </span>
            </span>
            <span v-if="item.tag" class="max-w-[40%] truncate text-xs font-normal text-muted-foreground">{{ item.tag }}</span>
            <span
              v-if="paletteSnippet(item, termsFor(item))"
              class="max-w-[40%] truncate text-xs font-normal text-muted-foreground"
              data-testid="command-palette-item-snippet"
            >
              <SearchHighlight :name="PALETTE_HIGHLIGHT" :terms="termsFor(item)">{{ paletteSnippet(item, termsFor(item)) }}</SearchHighlight>
            </span>
          </component>
        </section>
        <p v-if="search.searching.value" class="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground" data-testid="command-palette-searching">
          <ArrowPathIcon class="size-4 shrink-0 animate-spin" aria-hidden="true" />
          Searching…
        </p>
        <p v-else-if="unavailableMessage" class="px-3 py-2 text-sm text-muted-foreground" data-testid="command-palette-unavailable">
          {{ unavailableMessage }}
        </p>
        <p v-else-if="result.sections.length === 0" class="px-3 py-6 text-center text-sm text-muted-foreground">Nothing matches.</p>
      </div>
      <footer class="hidden shrink-0 flex-wrap items-center gap-5 border-t border-border/50 px-5 py-3 text-xs text-muted-foreground sm:flex">
        <span class="flex items-center gap-1.5"><kbd class="font-sans">↑</kbd><kbd class="font-sans">↓</kbd> Navigate</span>
        <span class="flex items-center gap-1.5"><kbd class="font-sans">↵</kbd> Open</span>
        <span class="flex items-center gap-1.5" data-testid="command-palette-peek-hint"><kbd class="font-sans">⌘↵</kbd> Side peek</span>
        <span class="flex items-center gap-1.5"><kbd class="font-sans">TAB</kbd> Category</span>
        <span class="flex items-center gap-1.5"><kbd class="font-sans">ESC</kbd> Close</span>
      </footer>
    </DialogContent>
  </Dialog>
  <BugReportDialog v-model:open="bugReportOpen" :user="user" />
</template>
